/**
 * InquiryDetailPage - Admin detail view for a single inquiry with response system
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import RichTextEditor from '../../../components/RichTextEditor';
import {
  getInquiry,
  updateInquiryStatus,
  respondToInquiry,
  assignInquiry,
  convertInquiryToBooking,
  getAdminUsers,
  type Inquiry,
  type AdminUser,
} from '../../../api/admin';
import { getEventAvailability } from '../../../api/events';
import { parseLocalDate } from '../../../utils/date';

export default function InquiryDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [inquiry, setInquiry] = useState<Inquiry | null>(null);
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Response form
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [responseSubject, setResponseSubject] = useState('');
  const [responseBody, setResponseBody] = useState('');
  const [includeBookingLink, setIncludeBookingLink] = useState(false);

  // Convert modal
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [convertDate, setConvertDate] = useState('');
  const [convertTimeSlot, setConvertTimeSlot] = useState<'9:00 AM' | '11:00 AM'>('9:00 AM');
  const [convertParticipants, setConvertParticipants] = useState('15');
  const [convertClassOptionId, setConvertClassOptionId] = useState('');
  const [convertClassLevel, setConvertClassLevel] = useState('all_levels');
  const [convertSkipDeposit, setConvertSkipDeposit] = useState(false);
  const [availableDates, setAvailableDates] = useState<string[]>([]);
  const [isLoadingDates, setIsLoadingDates] = useState(false);

  // Parse booking context from message for structured display
  function parseBookingContext(message: string | null | undefined): Record<string, string> {
    if (!message) return {};
    const match = message.match(/--- Booking Context ---\n([\s\S]+)$/);
    if (!match) return {};
    const lines = match[1]!.split('\n').filter(Boolean);
    const ctx: Record<string, string> = {};
    for (const line of lines) {
      const [key, ...val] = line.split(': ');
      if (key && val.length) ctx[key.trim()] = val.join(': ').trim();
    }
    return ctx;
  }

  function stripBookingContext(message: string | null | undefined): string {
    if (!message) return '';
    return message.replace(/\n\n--- Booking Context ---\n[\s\S]+$/, '').trim();
  }

  // Close modal
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [closeReason, setCloseReason] = useState('');

  useEffect(() => {
    if (id) {
      setResponseSubject('');
      setIncludeBookingLink(false);
      loadInquiry(id);
      loadAdminUsers();
    }
  }, [id]);

  // Auto-populate reply subject once when inquiry loads (SDA-114)
  useEffect(() => {
    if (!inquiry) return;
    if (responseSubject !== '') return;
    const showName = inquiry.show?.name ?? 'your';
    const cityFragment = inquiry.city?.name ? ` for ${inquiry.city.name}` : '';
    setResponseSubject(`Re: ${showName} inquiry${cityFragment}`);
  }, [inquiry, responseSubject]);

  // Load available dates when convert modal opens
  useEffect(() => {
    if (showConvertModal && inquiry?.tour_stop_id) {
      setIsLoadingDates(true);
      getEventAvailability(inquiry.tour_stop_id)
        .then((result) => {
          const uniqueDates = [...new Set(result.slots.map((s) => s.date))].sort();
          setAvailableDates(uniqueDates);
          if (uniqueDates.length > 0 && !convertDate && uniqueDates[0]) {
            setConvertDate(uniqueDates[0]);
          }
        })
        .catch(() => setAvailableDates([]))
        .finally(() => setIsLoadingDates(false));
    }
  }, [showConvertModal, inquiry?.tour_stop_id]);

  const loadInquiry = async (inquiryId: string) => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getInquiry(inquiryId);
      setInquiry(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load inquiry');
    } finally {
      setIsLoading(false);
    }
  };

  const loadAdminUsers = async () => {
    try {
      const users = await getAdminUsers();
      setAdminUsers(users);
    } catch (err) {
      console.error('Failed to load admin users:', err);
    }
  };

  const getSLAStatus = (inquiry: Inquiry) => {
    if (inquiry.status === 'responded' || inquiry.status === 'converted' || inquiry.status === 'closed') {
      return null;
    }

    const createdAt = new Date(inquiry.created_at);
    const now = new Date();
    const hoursOld = (now.getTime() - createdAt.getTime()) / (1000 * 60 * 60);

    if (inquiry.sla_breached_at) {
      return 'breached';
    } else if (hoursOld > 12) {
      return 'warning';
    }
    return 'on_track';
  };

  const getSLAInfo = (inquiry: Inquiry) => {
    const slaStatus = getSLAStatus(inquiry);
    if (!slaStatus) return null;

    const createdAt = new Date(inquiry.created_at);
    const now = new Date();
    const hoursOld = Math.floor((now.getTime() - createdAt.getTime()) / (1000 * 60 * 60));
    const hoursRemaining = 24 - hoursOld;

    const styles: Record<string, string> = {
      on_track: 'bg-success-100 border-success-300 text-success-800',
      warning: 'bg-warning-100 border-warning-300 text-warning-800',
      breached: 'bg-danger-100 border-danger-300 text-danger-800',
    };

    const messages: Record<string, string> = {
      on_track: `${hoursRemaining}h remaining to respond (24h target)`,
      warning: `${hoursOld}h elapsed - Response needed urgently!`,
      breached: `Overdue by ${hoursOld - 24}h — Immediate response required!`,
    };

    return (
      <div className={`p-4 rounded-lg border ${styles[slaStatus]}`}>
        <div className="flex items-center gap-2">
          {slaStatus === 'on_track' && (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          )}
          {slaStatus === 'warning' && (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          )}
          {slaStatus === 'breached' && (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          )}
          <span className="font-medium">{messages[slaStatus]}</span>
        </div>
      </div>
    );
  };

  const handleSendResponse = async () => {
    if (!id || !responseSubject || !responseBody) return;

    try {
      setIsSaving(true);
      await respondToInquiry(id, {
        subject: responseSubject,
        body: responseBody,
        include_booking_link: includeBookingLink,
      });
      await loadInquiry(id);
      setShowResponseModal(false);
      setResponseSubject('');
      setResponseBody('');
      setIncludeBookingLink(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send response');
    } finally {
      setIsSaving(false);
    }
  };

  const handleConvertToBooking = async () => {
    if (!id || !inquiry) return;

    const tourStopId = inquiry.tour_stop_id || (inquiry.tour_stop as any)?.id;
    if (!tourStopId) {
      setError('No tour stop associated with this inquiry');
      return;
    }
    if (!convertDate) {
      setError('Please select a class date');
      return;
    }
    if (!convertClassOptionId) {
      setError('Please select a class type');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);
      const booking = await convertInquiryToBooking(id, {
        tour_stop_id: tourStopId,
        class_option_id: convertClassOptionId,
        class_date: convertDate,
        time_slot: convertTimeSlot,
        participant_count: parseInt(convertParticipants) || 15,
        class_level: convertClassLevel,
        skip_deposit: convertSkipDeposit,
      });
      navigate(`/admin/bookings/${booking.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to convert inquiry');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAssign = async (userId: string) => {
    if (!id) return;

    try {
      await assignInquiry(id, userId);
      await loadInquiry(id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to assign inquiry');
    }
  };

  const handleClose = async () => {
    if (!id) return;

    try {
      setIsSaving(true);
      await updateInquiryStatus(id, 'closed');
      await loadInquiry(id);
      setShowCloseModal(false);
      setCloseReason('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to close inquiry');
    } finally {
      setIsSaving(false);
    }
  };

  const handleMarkInReview = async () => {
    if (!id) return;

    try {
      await updateInquiryStatus(id, 'in_review');
      await loadInquiry(id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update status');
    }
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      pending: 'bg-warning-100 text-warning-700',
      in_review: 'bg-primary-100 text-primary-700',
      responded: 'bg-success-100 text-success-700',
      converted: 'bg-green-100 text-green-700',
      closed: 'bg-warm-100 text-warm-600',
    };
    const labels: Record<string, string> = {
      pending: 'Pending',
      in_review: 'In Review',
      responded: 'Responded',
      converted: 'Converted',
      closed: 'Closed',
    };
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${styles[status] || 'bg-warm-100 text-warm-600'}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getSourceLabel = (source: string) => {
    const labels: Record<string, string> = {
      dual_option_hosted: 'Hosted Class Request',
      remote_request: 'Remote Studio Request',
      general: 'General Inquiry',
      contact_form: 'Help Request',
    };
    return labels[source] || source;
  };

  if (isLoading) {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-warm-200 rounded w-1/4 mb-4" />
        <div className="h-4 bg-warm-200 rounded w-1/2 mb-8" />
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="h-6 bg-warm-100 rounded w-1/3 mb-4" />
          <div className="h-4 bg-warm-100 rounded w-2/3 mb-2" />
          <div className="h-4 bg-warm-100 rounded w-1/2" />
        </div>
      </div>
    );
  }

  if (error && !inquiry) {
    return (
      <div className="bg-danger-50 border border-danger-200 rounded-lg p-6 text-center">
        <p className="text-danger-700">{error}</p>
        <button
          onClick={() => id && loadInquiry(id)}
          className="mt-4 px-4 py-2 bg-danger-600 text-white rounded-lg hover:bg-danger-700"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (!inquiry) return null;

  // SDA-213: allow follow-up responses after an inquiry has been responded to.
  const canRespond = ['pending', 'in_review', 'responded'].includes(inquiry.status);
  const isFollowUp = inquiry.status === 'responded';

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 sm:gap-3 mb-2">
            <Link
              to="/admin/inquiries"
              className="text-warm-600 hover:text-warm-900 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </Link>
            <h1 className="text-2xl sm:text-3xl font-display font-bold text-warm-900">
              Inquiry Details
            </h1>
            {getStatusBadge(inquiry.status)}
          </div>
          <p className="text-warm-600">
            Submitted {formatDate(inquiry.created_at)}
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          {inquiry.status === 'pending' && (
            <button
              onClick={handleMarkInReview}
              className="px-3 py-1.5 text-sm border border-warm-300 text-warm-700 rounded-lg hover:bg-warm-50 transition-colors"
            >
              Mark In Review
            </button>
          )}

          {canRespond && (
            <button
              onClick={() => setShowResponseModal(true)}
              className="btn-primary text-sm"
            >
              <svg className="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <span className="hidden sm:inline">Send </span>{isFollowUp ? 'Follow-up' : 'Response'}
            </button>
          )}

          {(inquiry.status === 'pending' || inquiry.status === 'in_review') &&
            inquiry.inquiry_source !== 'booking_question' && inquiry.studio_id && (
              <button
                onClick={() => setShowConvertModal(true)}
                className="px-3 py-1.5 text-sm bg-success-600 text-white rounded-lg hover:bg-success-700 transition-colors"
              >
                <svg className="w-4 h-4 mr-1.5 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="hidden sm:inline">Convert to </span>Booking
              </button>
            )}

          {inquiry.status !== 'closed' && inquiry.status !== 'converted' && (
            <button
              onClick={() => setShowCloseModal(true)}
              className="px-3 py-1.5 text-sm border border-danger-300 text-danger-700 rounded-lg hover:bg-danger-50 transition-colors"
            >
              Close<span className="hidden sm:inline"> Inquiry</span>
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-danger-50 border border-danger-200 rounded-lg text-danger-700">
          {error}
        </div>
      )}

      {/* SLA Alert */}
      {getSLAInfo(inquiry) && (
        <div className="mb-6">
          {getSLAInfo(inquiry)}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Inquiry Details */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-warm-900 mb-4">
              Inquiry Information
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
              <div>
                <p className="text-sm font-medium text-warm-500 mb-1">Source</p>
                <p className="text-warm-900">{getSourceLabel(inquiry.inquiry_source || 'general')}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-warm-500 mb-1">Show</p>
                <p className="text-warm-900">{(inquiry as any).show?.name || 'Not specified'}</p>
              </div>
              {/* Tour stop location */}
              {(inquiry as any).tour_stops && (
                <>
                  <div>
                    <p className="text-sm font-medium text-warm-500 mb-1">Venue / Theater</p>
                    <p className="text-warm-900">
                      {(inquiry as any).tour_stops.theaters?.name || 'N/A'}
                      {(inquiry as any).tour_stops.cities && (
                        <span className="text-warm-500 text-sm ml-1">
                          ({(inquiry as any).tour_stops.cities.name}, {(inquiry as any).tour_stops.cities.state})
                        </span>
                      )}
                    </p>
                  </div>
                  {((inquiry as any).tour_stops.start_date || (inquiry as any).tour_stops.end_date) && (
                    <div>
                      <p className="text-sm font-medium text-warm-500 mb-1">Tour Dates</p>
                      <p className="text-warm-900">
                        {(inquiry as any).tour_stops.start_date && parseLocalDate((inquiry as any).tour_stops.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        {(inquiry as any).tour_stops.end_date && ` – ${parseLocalDate((inquiry as any).tour_stops.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`}
                      </p>
                    </div>
                  )}
                </>
              )}
              {/* Distance & pricing tier */}
              {(inquiry as any).distance_miles != null && (
                <div>
                  <p className="text-sm font-medium text-warm-500 mb-1">Distance</p>
                  <p className="text-warm-900">{Math.round((inquiry as any).distance_miles)} miles</p>
                </div>
              )}
              {(inquiry as any).pricing_tier && (
                <div>
                  <p className="text-sm font-medium text-warm-500 mb-1">Pricing Tier</p>
                  <p className="text-warm-900 capitalize">{(inquiry as any).pricing_tier.replace(/_/g, ' ')}</p>
                </div>
              )}
              {/* Studio name — link to the studio profile when the inquiry is
                  tied to a studio; plain text for anonymous submissions. */}
              {(() => {
                const studioId = inquiry.studio?.id ?? inquiry.studio_id;
                const studioName = inquiry.studio?.name ?? inquiry.studio_name;
                if (!studioName) return null;
                return (
                  <div>
                    <p className="text-sm font-medium text-warm-500 mb-1">Studio Name</p>
                    {studioId ? (
                      <Link
                        to={`/admin/studios/${studioId}`}
                        className="text-primary-600 hover:text-primary-700 font-medium"
                      >
                        {studioName}
                      </Link>
                    ) : (
                      <p className="text-warm-900">{studioName}</p>
                    )}
                  </div>
                );
              })()}
              {/* Class specifics parsed from booking context */}
              {(() => {
                const ctx = parseBookingContext(inquiry.message);
                return (
                  <>
                    {ctx['Class'] && (
                      <div>
                        <p className="text-sm font-medium text-warm-500 mb-1">Class Type</p>
                        <p className="text-warm-900">{ctx['Class']}</p>
                      </div>
                    )}
                    {ctx['Date'] && (
                      <div>
                        <p className="text-sm font-medium text-warm-500 mb-1">Selected Date</p>
                        <p className="text-warm-900">{formatDate(ctx['Date'])}</p>
                      </div>
                    )}
                    {ctx['Time'] && (
                      <div>
                        <p className="text-sm font-medium text-warm-500 mb-1">Selected Time</p>
                        <p className="text-warm-900">{ctx['Time']}</p>
                      </div>
                    )}
                  </>
                );
              })()}
              {/* Preferred dates */}
              {inquiry.preferred_dates && (
                (() => {
                  const raw = inquiry.preferred_dates;
                  const dates = Array.isArray(raw)
                    ? raw
                    : (() => { try { const p = JSON.parse(raw as unknown as string); return Array.isArray(p) ? p : []; } catch { return []; } })();
                  if (dates.length === 0) return null;
                  return (
                    <div>
                      <p className="text-sm font-medium text-warm-500 mb-1">Preferred Dates</p>
                      <div className="space-y-1">
                        {dates.map((date: string, i: number) => (
                          <p key={i} className="text-warm-900 text-sm">{formatDate(date)}</p>
                        ))}
                      </div>
                    </div>
                  );
                })()
              )}
            </div>

            {/* Message */}
            <div>
              <p className="text-sm font-medium text-warm-500 mb-2">Message</p>
              <div className="bg-warm-50 rounded-lg p-4">
                <p className="text-warm-900 whitespace-pre-wrap">
                  {stripBookingContext(inquiry.message) || 'No message provided.'}
                </p>
              </div>
            </div>

            {/* Hosted Class Venue Details (from flat DB columns) */}
            {inquiry.inquiry_source === 'dual_option_hosted' && ((inquiry as any).venue_name || (inquiry as any).venue_address) && (
              <div className="mt-6 pt-6 border-t border-warm-100">
                <h3 className="font-semibold text-warm-900 mb-4">Proposed Venue (Hosted Class)</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {(inquiry as any).venue_name && (
                    <div>
                      <p className="text-sm font-medium text-warm-500 mb-1">Venue Name</p>
                      <p className="text-warm-900">{(inquiry as any).venue_name}</p>
                    </div>
                  )}
                  {(inquiry as any).venue_address && (
                    <div>
                      <p className="text-sm font-medium text-warm-500 mb-1">Address</p>
                      <p className="text-warm-900">{(inquiry as any).venue_address}</p>
                    </div>
                  )}
                  {(inquiry as any).venue_capacity && (
                    <div>
                      <p className="text-sm font-medium text-warm-500 mb-1">Capacity</p>
                      <p className="text-warm-900">{(inquiry as any).venue_capacity} students</p>
                    </div>
                  )}
                  {(inquiry as any).parking_available != null && (
                    <div>
                      <p className="text-sm font-medium text-warm-500 mb-1">Parking Available</p>
                      <p className="text-warm-900">{(inquiry as any).parking_available ? 'Yes' : 'No'}</p>
                    </div>
                  )}
                  {(inquiry as any).studio_will_travel != null && (
                    <div>
                      <p className="text-sm font-medium text-warm-500 mb-1">Will Travel</p>
                      <p className="text-warm-900">{(inquiry as any).studio_will_travel ? 'Yes' : 'No'}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Response History */}
          {inquiry.responses && inquiry.responses.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-lg font-semibold text-warm-900 mb-4">
                Response History
              </h2>
              <div className="space-y-4">
                {inquiry.responses.map((response, index) => (
                  <div key={index} className="border border-warm-100 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-medium text-warm-900">
                        {response.admin_user?.name || 'Admin'}
                      </p>
                      <p className="text-sm text-warm-500">
                        {formatDate(response.sent_at)}
                      </p>
                    </div>
                    <p className="text-sm font-medium text-warm-700 mb-1">
                      Subject: {response.subject}
                    </p>
                    <div
                      className="text-warm-600 text-sm prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: response.body ?? '' }}
                    />
                    {response.included_booking_link && (
                      <p className="text-xs text-primary-600 mt-2">
                        ✓ Included booking link
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Activity Timeline */}
          {inquiry.activity_log && inquiry.activity_log.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-lg font-semibold text-warm-900 mb-4">
                Activity Timeline
              </h2>
              <div className="space-y-4">
                {inquiry.activity_log.map((activity, index) => (
                  <div key={index} className="flex gap-3">
                    <div className="w-2 h-2 rounded-full bg-primary-500 mt-2" />
                    <div>
                      <p className="text-warm-900">{activity.description}</p>
                      <p className="text-sm text-warm-500">
                        {formatDate(activity.created_at)}
                        {activity.user && ` by ${activity.user.name}`}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Contact Info */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-warm-900 mb-4">
              Contact Information
            </h2>

            {inquiry.studio ? (
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-warm-500 mb-1">Studio</p>
                  <Link
                    to={`/admin/studios/${inquiry.studio.id}`}
                    className="text-primary-600 hover:text-primary-700 font-medium"
                  >
                    {inquiry.studio.name}
                  </Link>
                </div>
                {inquiry.studio.primary_contact && (
                  <>
                    <div>
                      <p className="text-sm font-medium text-warm-500 mb-1">Contact Name</p>
                      <p className="text-warm-900">{inquiry.studio.primary_contact.name}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-warm-500 mb-1">Email</p>
                      <a
                        href={`mailto:${inquiry.studio.primary_contact.email}`}
                        className="text-primary-600 hover:text-primary-700"
                      >
                        {inquiry.studio.primary_contact.email}
                      </a>
                    </div>
                    {inquiry.studio.primary_contact.phone && (
                      <div>
                        <p className="text-sm font-medium text-warm-500 mb-1">Phone</p>
                        <a
                          href={`tel:${inquiry.studio.primary_contact.phone}`}
                          className="text-primary-600 hover:text-primary-700"
                        >
                          {inquiry.studio.primary_contact.phone}
                        </a>
                      </div>
                    )}
                  </>
                )}
                {inquiry.studio.zip_code && (
                  <div>
                    <p className="text-sm font-medium text-warm-500 mb-1">Studio Zip Code</p>
                    <a
                      href={`/events?zip=${encodeURIComponent(inquiry.studio.zip_code)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 hover:text-primary-700"
                    >
                      {inquiry.studio.zip_code} — View classes near this zip →
                    </a>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-warm-500 mb-1">Name</p>
                  <p className="text-warm-900">{inquiry.contact_name}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-warm-500 mb-1">Email</p>
                  <a
                    href={`mailto:${inquiry.contact_email}`}
                    className="text-primary-600 hover:text-primary-700"
                  >
                    {inquiry.contact_email}
                  </a>
                </div>
                {inquiry.contact_phone && (
                  <div>
                    <p className="text-sm font-medium text-warm-500 mb-1">Phone</p>
                    <a
                      href={`tel:${inquiry.contact_phone}`}
                      className="text-primary-600 hover:text-primary-700"
                    >
                      {inquiry.contact_phone}
                    </a>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Assignment */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-warm-900 mb-4">
              Assignment
            </h2>

            {inquiry.assigned_to ? (
              (() => {
                const assignedUser = adminUsers.find((u) => u.id === inquiry.assigned_to);
                if (!assignedUser) return <p className="text-warm-500 mb-4">Unknown user</p>;
                return (
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="text-primary-700 font-semibold">
                        {assignedUser.name?.charAt(0) || assignedUser.first_name?.charAt(0) || 'A'}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-warm-900">{assignedUser.name || `${assignedUser.first_name} ${assignedUser.last_name}`}</p>
                      <p className="text-sm text-warm-500">{assignedUser.email}</p>
                    </div>
                  </div>
                );
              })()
            ) : (
              <p className="text-warm-500 mb-4">Unassigned</p>
            )}

            <select
              value={inquiry.assigned_to || ''}
              onChange={(e) => handleAssign(e.target.value)}
              className="w-full px-3 py-2 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="">Unassigned</option>
              {adminUsers.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name}
                </option>
              ))}
            </select>
          </div>

          {/* Linked Booking */}
          {(inquiry.booking_id || inquiry.linked_booking) && (
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-lg font-semibold text-warm-900 mb-4">
                {inquiry.inquiry_source === 'booking_question' ? 'Related Booking' : 'Converted Booking'}
              </h2>
              <Link
                to={`/admin/bookings/${inquiry.linked_booking?.id || inquiry.booking_id}`}
                className="flex items-center gap-2 text-primary-600 hover:text-primary-700"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                {inquiry.linked_booking?.booking_number
                  ? `Booking #${inquiry.linked_booking.booking_number}`
                  : 'View Booking'}
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Response Modal */}
      {showResponseModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-warm-100">
              <h2 className="text-xl font-semibold text-warm-900">{isFollowUp ? 'Send Follow-up' : 'Send Response'}</h2>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-warm-700 mb-1">
                  Subject *
                </label>
                <input
                  type="text"
                  value={responseSubject}
                  onChange={(e) => setResponseSubject(e.target.value)}
                  className="w-full px-3 py-2 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Re: Your inquiry about..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-warm-700 mb-1">
                  Message *
                </label>
                <RichTextEditor
                  value={responseBody}
                  onChange={setResponseBody}
                  placeholder="Write your response here..."
                />
              </div>

              {inquiry.inquiry_source !== 'booking_question' && !!inquiry.tour_stop_id && (
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="includeBookingLink"
                    checked={includeBookingLink}
                    onChange={(e) => setIncludeBookingLink(e.target.checked)}
                    className="w-4 h-4 text-primary-600 border-warm-300 rounded focus:ring-primary-500"
                  />
                  <label htmlFor="includeBookingLink" className="text-sm text-warm-700">
                    Include direct booking link in email
                  </label>
                </div>
              )}

              {/* Preview */}
              <div className="bg-warm-50 rounded-lg p-4">
                <p className="text-sm font-medium text-warm-700 mb-2">Preview:</p>
                <p className="text-sm text-warm-600">
                  To: {inquiry.studio?.primary_contact?.email || inquiry.contact_email}
                </p>
                <p className="text-sm text-warm-600">
                  Subject: {responseSubject || '(No subject)'}
                </p>
              </div>
            </div>

            <div className="p-6 border-t border-warm-100 flex justify-end gap-3">
              <button
                onClick={() => setShowResponseModal(false)}
                className="px-4 py-2 text-warm-700 hover:bg-warm-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSendResponse}
                disabled={isSaving || !responseSubject || !responseBody}
                className="btn-primary disabled:opacity-50"
              >
                {isSaving ? 'Sending...' : isFollowUp ? 'Send Follow-up' : 'Send Response'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Convert Modal */}
      {showConvertModal && (() => {
        // Get class options from the show
        const classOptions = (inquiry.show as any)?.class_options as { id: string; name: string; duration_minutes: number; base_price_cents: number }[] | undefined;
        const selectedOption = classOptions?.find(o => o.id === convertClassOptionId);

        return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-warm-100">
              <h2 className="text-xl font-semibold text-warm-900">Convert to Booking</h2>
              <p className="text-sm text-warm-500 mt-1">
                Create a booking from this inquiry. The studio and show will be pre-filled.
              </p>
            </div>

            <div className="p-6 space-y-5">
              {error && (
                <div className="p-3 bg-danger-50 border border-danger-200 rounded-lg text-sm text-danger-700">
                  {error}
                </div>
              )}

              {/* Pre-filled info */}
              <div className="bg-warm-50 rounded-lg p-4">
                <p className="text-xs font-medium uppercase tracking-wide text-warm-500 mb-2">Inquiry Details</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-warm-500">Studio:</span>{' '}
                    <span className="text-warm-900 font-medium">{inquiry.studio?.name || inquiry.contact_name}</span>
                  </div>
                  <div>
                    <span className="text-warm-500">Show:</span>{' '}
                    <span className="text-warm-900 font-medium">{inquiry.show?.name || 'N/A'}</span>
                  </div>
                  {inquiry.tour_stop && (
                    <div className="col-span-2">
                      <span className="text-warm-500">Venue:</span>{' '}
                      <span className="text-warm-900 font-medium">
                        {(inquiry.tour_stop as any)?.theaters?.name || (inquiry.tour_stop as any)?.cities?.name}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Class Date Selection */}
              <div>
                <label className="block text-sm font-medium text-warm-700 mb-2">
                  Class Date *
                </label>
                {isLoadingDates ? (
                  <p className="text-sm text-warm-500">Loading available dates...</p>
                ) : availableDates.length > 0 ? (
                  <select
                    value={convertDate}
                    onChange={(e) => setConvertDate(e.target.value)}
                    className="w-full px-3 py-2 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                  >
                    {availableDates.map((d) => (
                      <option key={d} value={d}>
                        {new Date(d + 'T00:00:00').toLocaleDateString('en-US', {
                          weekday: 'long',
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="space-y-2">
                    <p className="text-xs text-warm-500">No scheduled dates found. Enter a date manually:</p>
                    <input
                      type="date"
                      value={convertDate}
                      onChange={(e) => setConvertDate(e.target.value)}
                      className="w-full px-3 py-2 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                    />
                  </div>
                )}
              </div>

              {/* Class Type */}
              {classOptions && classOptions.length > 0 ? (
                <div>
                  <label className="block text-sm font-medium text-warm-700 mb-2">
                    Class Type *
                  </label>
                  <div className="grid gap-2">
                    {classOptions.map((opt) => {
                      const isSelected = convertClassOptionId === opt.id;
                      return (
                        <button
                          key={opt.id}
                          type="button"
                          onClick={() => setConvertClassOptionId(opt.id)}
                          className={`text-left px-4 py-3 rounded-lg border-2 transition-all ${
                            isSelected
                              ? 'border-primary-500 bg-primary-50'
                              : 'border-warm-200 hover:border-warm-300'
                          }`}
                        >
                          <div className="flex justify-between items-center">
                            <span className="font-medium text-warm-900">{opt.name}</span>
                            <span className="text-sm text-warm-600">
                              ${(opt.base_price_cents / 100).toFixed(0)} &middot; {opt.duration_minutes} min
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="p-4 bg-warning-50 border border-warning-200 rounded-lg">
                  <p className="text-sm font-medium text-warning-800">No class types configured</p>
                  <p className="text-xs text-warning-600 mt-1">
                    This show doesn't have any class options set up yet. Please add class options to the show before converting.
                  </p>
                </div>
              )}

              {/* Time Slot */}
              <div>
                <label className="block text-sm font-medium text-warm-700 mb-2">
                  Time Slot *
                </label>
                <div className="flex gap-3">
                  {(['9:00 AM', '11:00 AM'] as const).map((slot) => (
                    <button
                      key={slot}
                      type="button"
                      onClick={() => setConvertTimeSlot(slot)}
                      className={`flex-1 px-4 py-2.5 rounded-lg border-2 text-sm font-medium transition-all ${
                        convertTimeSlot === slot
                          ? 'border-primary-500 bg-primary-50 text-primary-900'
                          : 'border-warm-200 hover:border-warm-300 text-warm-700'
                      }`}
                    >
                      {slot}
                    </button>
                  ))}
                </div>
              </div>

              {/* Participants */}
              <div>
                <label className="block text-sm font-medium text-warm-700 mb-1">
                  Number of Participants *
                </label>
                <input
                  type="number"
                  value={convertParticipants}
                  onChange={(e) => setConvertParticipants(e.target.value)}
                  min="1"
                  max="50"
                  className="w-full px-3 py-2 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                />
              </div>

              {/* Class Level */}
              <div>
                <label className="block text-sm font-medium text-warm-700 mb-2">
                  Class Level *
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {([
                    { value: 'all_levels', label: 'All Levels' },
                    { value: 'beginner', label: 'Beginner' },
                    { value: 'intermediate', label: 'Intermediate' },
                    { value: 'advanced', label: 'Advanced' },
                  ] as const).map((level) => (
                    <button
                      key={level.value}
                      type="button"
                      onClick={() => setConvertClassLevel(level.value)}
                      className={`px-3 py-2 rounded-lg border-2 text-sm font-medium transition-all ${
                        convertClassLevel === level.value
                          ? 'border-primary-500 bg-primary-50 text-primary-900'
                          : 'border-warm-200 hover:border-warm-300 text-warm-700'
                      }`}
                    >
                      {level.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Skip Deposit */}
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={convertSkipDeposit}
                  onChange={(e) => setConvertSkipDeposit(e.target.checked)}
                  className="w-4 h-4 rounded border-warm-300 text-primary-600 focus:ring-primary-500"
                />
                <div>
                  <span className="text-sm text-warm-700">Skip deposit requirement</span>
                  <span className="text-xs text-warm-500 block">Mark booking as confirmed without deposit</span>
                </div>
              </label>

              {/* Price Summary */}
              {selectedOption && (
                <div className="bg-success-50 rounded-lg p-4 border border-success-200">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-success-700">Class Price</span>
                    <span className="font-semibold text-success-900">${(selectedOption.base_price_cents / 100).toFixed(2)}</span>
                  </div>
                  {!convertSkipDeposit && (
                    <div className="flex justify-between items-center text-sm mt-1">
                      <span className="text-success-700">Deposit Due</span>
                      <span className="font-medium text-success-800">$100.00</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="p-6 border-t border-warm-100 flex justify-end gap-3">
              <button
                onClick={() => { setShowConvertModal(false); setError(null); }}
                className="px-4 py-2 text-warm-700 hover:bg-warm-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConvertToBooking}
                disabled={isSaving || !convertDate || !convertClassOptionId}
                className="px-4 py-2 bg-success-600 text-white rounded-lg hover:bg-success-700 disabled:opacity-50 transition-colors"
              >
                {isSaving ? 'Converting...' : 'Create Booking'}
              </button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Close Modal */}
      {showCloseModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 overflow-y-auto max-h-[90vh]">
            <div className="p-6 border-b border-warm-100">
              <h2 className="text-xl font-semibold text-warm-900">Close Inquiry</h2>
            </div>

            <div className="p-6">
              <p className="text-warm-600 mb-4">
                Are you sure you want to close this inquiry? Please provide a reason.
              </p>

              <div>
                <label className="block text-sm font-medium text-warm-700 mb-1">
                  Reason for closing
                </label>
                <select
                  value={closeReason}
                  onChange={(e) => setCloseReason(e.target.value)}
                  className="w-full px-3 py-2 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">Select a reason</option>
                  <option value="no_response">No response from studio</option>
                  <option value="not_interested">Studio no longer interested</option>
                  <option value="booked_elsewhere">Booked through another channel</option>
                  <option value="duplicate">Duplicate inquiry</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            <div className="p-6 border-t border-warm-100 flex justify-end gap-3">
              <button
                onClick={() => setShowCloseModal(false)}
                className="px-4 py-2 text-warm-700 hover:bg-warm-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleClose}
                disabled={isSaving || !closeReason}
                className="px-4 py-2 bg-danger-600 text-white rounded-lg hover:bg-danger-700 disabled:opacity-50 transition-colors"
              >
                {isSaving ? 'Closing...' : 'Close Inquiry'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
